/**
 * Profile fields as people type them, shared by You and setup: sex, birth year and height can be
 * left blank (unknown), and anything typed must make sense. Pure, so tests run without a browser.
 */
import { parseDecimal } from '../../shared/numbers';
import type { Basic } from '../../shared/targets';
import { cmToFtIn, fromDisplayWeight, ftInToCm, type WeightUnit } from '../../shared/units';

/** A height as typed: feet and inches, and centimetres. */
export interface HeightText {
  ft: string;
  inch: string;
  cm: string;
}

/** A height as text in every unit (feet and inches, and centimetres), so it reads right whichever unit is chosen. */
export function heightText(heightCm: number | null): HeightText {
  if (heightCm === null) return { ft: '', inch: '', cm: '' };
  const { ft, inch } = cmToFtIn(heightCm);
  return { ft: String(ft), inch: String(inch), cm: String(Math.round(heightCm)) };
}

/** A birth year someone typed: a number in range, null when blank, "invalid" otherwise. */
export function parseBirthYear(text: string, thisYear: number): number | null | 'invalid' {
  if (!text.trim()) return null;
  const n = Number(text.trim());
  return Number.isInteger(n) && n >= 1900 && n <= thisYear - 13 ? n : 'invalid';
}

/** Height in cm from feet and inches (lb) or centimetres (kg): null when blank, "invalid" out of 100–250 cm. */
export function parseHeight(a: HeightText & { unit: WeightUnit }): number | null | 'invalid' {
  if (a.unit === 'kg') {
    if (!a.cm.trim()) return null;
    const cm = parseDecimal(a.cm);
    return cm !== null && cm >= 100 && cm <= 250 ? Math.round(cm * 10) / 10 : 'invalid';
  }
  if (!a.ft.trim() && !a.inch.trim()) return null;
  const ft = a.ft.trim() ? parseDecimal(a.ft) : 0;
  const inch = a.inch.trim() ? parseDecimal(a.inch) : 0;
  if (ft === null || inch === null || ft < 0 || inch < 0 || inch >= 12) return 'invalid';
  const cm = ftInToCm(ft, inch);
  return cm >= 100 && cm <= 250 ? Math.round(cm * 10) / 10 : 'invalid';
}

/** A body weight in kg from what's typed in the person's unit: null when blank, "invalid" out of 20–400 kg. */
export function parseWeightKg(text: string, unit: WeightUnit): number | null | 'invalid' {
  if (!text.trim()) return null;
  const n = parseDecimal(text);
  if (n === null || n <= 0) return 'invalid';
  const kg = fromDisplayWeight(n, unit);
  return kg >= 20 && kg <= 400 ? Math.round(kg * 100) / 100 : 'invalid';
}

/** "your height and today's weight". */
export function basicsInWords(missing: readonly Basic[]): string {
  const words: Record<Basic, string> = { sex: 'your sex', birthYear: 'your birth year', height: 'your height', weight: "today's weight" };
  const list = missing.map((m) => words[m]);
  return list.length <= 1 ? (list[0] ?? '') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}
